import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, X } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCENT = "#FF3B30";
const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error

  const poll = useCallback(
    async (attempt) => {
      if (!sessionId) {
        setState("error");
        return;
      }
      if (attempt >= MAX_POLLS) {
        setState("pending");
        return;
      }
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
        setTimeout(() => poll(attempt + 1), 2000);
      } catch (e) {
        setState("error");
      }
    },
    [sessionId]
  );

  useEffect(() => {
    poll(0);
  }, [poll]);

  const content = {
    checking: {
      icon: <Loader2 size={40} className="animate-spin" />,
      title: "Confirming Payment",
      body: "Hang tight — we're verifying your payment with Stripe.",
    },
    paid: {
      icon: <Check size={40} style={{ color: ACCENT }} />,
      title: "Payment Confirmed",
      body: "Welcome aboard. Our team will reach out within 48 hours to kick off your Basic Marketing package.",
    },
    pending: {
      icon: <Loader2 size={40} />,
      title: "Still Processing",
      body: "Your payment is taking a moment to confirm. You'll get an update shortly — no need to pay again.",
    },
    error: {
      icon: <X size={40} style={{ color: ACCENT }} />,
      title: "Something Went Wrong",
      body: "We couldn't confirm this payment. If you were charged, contact us and we'll sort it out.",
    },
  }[state];

  return (
    <div data-testid="payment-success-page" className="min-h-screen grain bg-[#050505] flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-lg w-full border-2 border-zinc-700 bg-black p-10 md:p-16 text-center"
      >
        <div className="flex justify-center mb-8 text-zinc-100">{content.icon}</div>
        <h1 data-testid="payment-status-title" className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl text-zinc-50">
          {content.title}
        </h1>
        <p className="font-mono-b text-sm font-light text-zinc-400 mt-6 leading-relaxed">{content.body}</p>
        <Link
          to="/"
          data-testid="back-home-link"
          className="btn-brutal inline-block mt-10 font-mono-b text-sm uppercase tracking-[0.2em] px-8 py-4 border-2 border-zinc-200 text-zinc-50"
        >
          Back To Home
        </Link>
      </motion.div>
    </div>
  );
}
